import React, { useState, useCallback } from 'react';
import { DatePicker, Button, Table, Statistic, Row, Col, Card, Icon, Modal, message, Alert } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import numeral from 'numeral';
import api from 'api';
import ErrorMessage from 'components/ErrorMessage';

// Preview screen for the batch contract-billing engine (procedures/contracts.js
// generateContractInvoices). It calls the SAME RPC the cron job will call, with
// dry_run=true, so what you see here is exactly what a live run on `as_of_date`
// would generate (the backend runs the whole batch inside a tx and rolls it back).
//
// Default run date is the 1st of NEXT month (the batch is meant to run monthly),
// matching decision "date picker defaulting to the 1st of next month plus Download CSV".
//
// Superuser-only for now, same gate as the Generate Invoice action and the RPC
// itself (RPC_generateContractInvoices is granted to 0 roles; superuser bypasses).

function firstOfNextMonth() {
    return moment().add(1, 'month').startOf('month');
}

const columns = [
    {title: 'Contract #', dataIndex: 'contract_number', key: 'contract_number',
     render: (v) => <span style={{fontWeight: 600}}>{v}</span>},
    {title: 'Site', dataIndex: 'site', key: 'site',
     render: (v) => v || <span style={{color: '#bbb'}}>(No Site)</span>},
    {title: 'Bill-To', dataIndex: 'bill_to_customer', key: 'bill_to_customer',
     render: (v) => v || <span style={{color: '#bbb'}}>(No Customer)</span>},
    {title: 'Period Start', dataIndex: 'period_start', key: 'period_start',
     render: (v) => v ? moment(v).format('MMM DD, YYYY') : ''},
    {title: 'Period End', dataIndex: 'period_end', key: 'period_end',
     render: (v) => v ? moment(v).format('MMM DD, YYYY') : ''},
    {title: 'Cycle', dataIndex: 'billing_cycle', key: 'billing_cycle'},
    {title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right',
     render: (v) => `$${numeral(v).format('0,0.00')}`},
    // grouping_key ties rows that share one physical invoice (grouped billing).
    {title: 'Invoice Group', dataIndex: 'grouping_key', key: 'grouping_key',
     render: (v) => <span style={{color: '#888', fontSize: '12px'}}>{v}</span>},
];

function downloadCsv(csv, asOf) {
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upcoming_contract_invoices_${asOf}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function UpcomingContractInvoices() {
    const [runDate, setRunDate] = useState(firstOfNextMonth());
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    // Shared caller for both the preview (dry_run=true) and the live batch (dry_run=false).
    // Returns the report result on success, or null after setting an error.
    const runBatch = useCallback(async (as_of_date, dry_run) => {
        const response = await api.rpc('generateContractInvoices', {as_of_date, dry_run});
        const envelope = (response && response.body) || {};
        if (response && response.success && envelope.result && !envelope.error) {
            return envelope.result;
        }
        const msg = (envelope.error && envelope.error.message)
            || (response && response.errors && response.errors[0] && response.errors[0].message)
            || (dry_run ? 'The preview could not be generated.' : 'The invoices could not be generated.');
        setError(msg);
        return null;
    }, []);

    const preview = useCallback(async () => {
        if (!runDate) {
            setError('Pick a run date first.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await runBatch(runDate.format('YYYY-MM-DD'), true);
            setReport(result);
        } catch (e) {
            setError(String(e && e.message ? e.message : e));
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [runDate, runBatch]);

    // Live generation. Runs against the exact date that was previewed (report.as_of_date),
    // NOT the current picker value, so what the user confirmed is what gets billed. The RPC
    // is idempotent, so a re-run after this generates 0 -- we re-preview afterward to show that.
    const generate = useCallback(async () => {
        if (!report || report.dry_run === false) return;
        const as_of_date = report.as_of_date;
        setGenerating(true);
        setError(null);
        try {
            const live = await runBatch(as_of_date, false);
            if (live) {
                setReport(live);
                message.success(
                    `Generated ${live.invoice_count} invoice${live.invoice_count === 1 ? '' : 's'} ` +
                    `(${live.line_count} line item${live.line_count === 1 ? '' : 's'}, ` +
                    `$${numeral(live.total_amount).format('0,0.00')}).`, 6);
            }
        } catch (e) {
            setError(String(e && e.message ? e.message : e));
        } finally {
            setGenerating(false);
        }
    }, [report, runBatch]);

    const confirmGenerate = useCallback(() => {
        if (!report) return;
        Modal.confirm({
            title: 'Generate these invoices?',
            content: `This will create ${report.invoice_count} real invoice` +
                `${report.invoice_count === 1 ? '' : 's'} ` +
                `(${report.line_count} line item${report.line_count === 1 ? '' : 's'}, ` +
                `$${numeral(report.total_amount).format('0,0.00')}) dated to each period's start, ` +
                `for the run date ${moment(report.as_of_date).format('MMM DD, YYYY')}. ` +
                `Invoices enter accounting immediately and can only be voided, not deleted.`,
            okText: 'Generate Invoices',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: generate,
        });
    }, [report, generate]);

    const rows = (report && report.invoices) || [];
    const isLive = report && report.dry_run === false;

    return <div style={{margin: 'auto', maxWidth: '1150px', padding: '24px'}}>
        <h1 style={{fontFamily: 'Raleway'}}>Upcoming Contract Invoices</h1>
        <p style={{color: '#666', maxWidth: '760px'}}>
            Preview every contract invoice the monthly billing job would generate if it ran
            on the selected date. This is a dry run — nothing is created, numbered, or sent.
            Any period starting on or before the run date is included, so back-dated contracts
            may show more than one invoice.
        </p>

        <div style={{display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '24px'}}>
            <div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>Run Date</div>
                <DatePicker
                    value={runDate}
                    onChange={setRunDate}
                    allowClear={false}
                    format="MMM DD, YYYY"
                    style={{width: '180px'}} />
            </div>
            <Button type="primary" icon="calculator" loading={loading} onClick={preview}>
                Preview Invoices
            </Button>
            {report && rows.length > 0 && !isLive &&
                <Button type="danger" icon="thunderbolt" loading={generating}
                        onClick={confirmGenerate}>
                    Generate Invoices
                </Button>}
            {report && rows.length > 0 &&
                <Button icon="download"
                        onClick={() => downloadCsv(report.csv, report.as_of_date)}>
                    Download CSV
                </Button>}
        </div>

        {error && <ErrorMessage errors={error} />}

        {report && <div>
            {isLive &&
                <Alert type="success" showIcon style={{marginBottom: '16px'}}
                    message={`Invoices generated for ${moment(report.as_of_date).format('MMM DD, YYYY')}.`}
                    description="These are live invoices now in accounting. Re-previewing this date will show nothing left to generate (the batch is idempotent)." />}
            <Row gutter={16} style={{marginBottom: '24px'}}>
                <Col span={8}><Card><Statistic title="Invoices" value={report.invoice_count} /></Card></Col>
                <Col span={8}><Card><Statistic title="Line Items" value={report.line_count} /></Card></Col>
                <Col span={8}><Card><Statistic title="Total"
                    value={report.total_amount} precision={2} prefix="$" /></Card></Col>
            </Row>

            {rows.length === 0
                ? <Card style={{textAlign: 'center', color: '#888'}}>
                    <Icon type="check-circle" style={{fontSize: '32px', color: '#52c41a'}} />
                    <div style={{marginTop: '8px'}}>
                        No invoices would be generated on {moment(report.as_of_date).format('MMM DD, YYYY')}.
                    </div>
                  </Card>
                : <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey={(r, i) => `${r.contract_number}_${r.period_start}_${i}`}
                    pagination={false}
                    size="middle"
                    rowClassName={(r, i) => i % 2 ? 'row-alt' : ''} />}
        </div>}
    </div>;
}

export default withRouter(UpcomingContractInvoices);
